/**
 * Shared basket (ClickUp 869c8xueu).
 *
 * Cart-page feature backed by the "Ilva - Shared Basket" app proxy
 * (/apps/shared-basket/baskets -> draft orders). Three custom elements, all
 * rendered by sections/main-cart.liquid:
 *
 *  - <share-basket>          dialog opened from the "Save and share cart" link
 *                            ([data-share-cart], document-level delegation so it
 *                            survives cart section re-renders). Saves the current
 *                            cart, shows basket ID + share link + QR code, and
 *                            clears the cart only after the basket was created.
 *  - <shared-basket-restore> banner shown when the URL has ?shared_basket=ID.
 *                            Fetches the basket and, on confirmation, replaces the
 *                            current cart with the basket's items.
 *  - <saved-baskets>         "Previously saved baskets" list for logged-in
 *                            customers, loaded from the app proxy.
 *
 * getSellerId() reads the current customer's seller ID (from the #share-basket-dialog
 * data-seller-id attribute, sourced server-side from customer.metafields.custom.sales_person_id).
 * When present it is written to the live cart as the `seller_id` attribute — both when
 * sharing (shareCart) and when a basket is restored back into the cart (applyBasketToCart),
 * so the current viewer's seller ID wins over whatever the basket was saved with. When
 * restoring without a current seller (e.g. a customer opening a shared basket link), the
 * original sharer's seller_id survives from basket.attributes instead. Either way, the
 * custom.sales_person_id cart metafield (setCartSellerMetafield) is kept in sync with
 * whichever seller_id value actually ends up on the cart attribute.
 *
 * ensureSellerAttribute() covers the rest of the storefront: it attaches the same
 * attribute/metafield to a logged-in seller's cart on ordinary add-to-cart / checkout,
 * not just share/restore. It runs once on page load and again on the theme's on:cart:add
 * and on:line-item:change events (assets/custom.js), so a cart that was empty at page
 * load still gets attributed as soon as it receives its first item.
 */
(() => {
  const BASKET_ID_PATTERN = /^[A-Z2-9]{4}-[A-Z2-9]{4}$/;
  const RESTORE_WARNING_KEY = 'shared_basket_restore_warning';
  const OPEN_DRAWER_KEY = 'shared_basket_open_drawer';

  const shopifyRoot = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  /**
   * Reads the current customer's seller ID from the global share-basket dialog, populated
   * server-side from customer.metafields.custom.sales_person_id (empty for regular customers).
   * @returns {string}
   */
  function getSellerId() {
    const dialog = document.getElementById('share-basket-dialog');
    return (dialog && dialog.dataset.sellerId) || '';
  }

  const CART_SELLER_METAFIELD_NAMESPACE = 'custom';
  const CART_SELLER_METAFIELD_KEY = 'sales_person_id';

  const CART_METAFIELDS_SET_MUTATION = `
    mutation setCartSellerMetafield($ownerId: ID!, $value: String!) {
      cartMetafieldsSet(metafields: [{
        ownerId: $ownerId
        key: "${CART_SELLER_METAFIELD_NAMESPACE}.${CART_SELLER_METAFIELD_KEY}"
        type: "single_line_text_field"
        value: $value
      }]) {
        metafields { id namespace key value }
        userErrors { field message }
      }
    }
  `;

  /**
   * Sets the custom.sales_person_id cart metafield via the Storefront API, so it can be
   * auto-copied onto the order metafield of the same name at checkout ("cart to order
   * copyable"). Best-effort: the seller_id cart attribute set alongside it already carries
   * the value through checkout as a note attribute, so a failure here is not fatal.
   * @param {string} cartToken - The cart's AJAX API token (used as the Storefront Cart ID).
   * @param {string} sellerId - The seller ID to store.
   */
  async function setCartSellerMetafield(cartToken, sellerId) {
    const dialog = document.getElementById('share-basket-dialog');
    const shopDomain = dialog && dialog.dataset.shopDomain;
    const storefrontToken = dialog && dialog.dataset.storefrontToken;
    if (!shopDomain || !storefrontToken || !cartToken) return;

    try {
      const response = await fetch(`https://${shopDomain}/api/2026-07/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontToken
        },
        body: JSON.stringify({
          query: CART_METAFIELDS_SET_MUTATION,
          variables: { ownerId: `gid://shopify/Cart/${cartToken}`, value: sellerId }
        })
      });
      const { data, errors } = await response.json();
      const userErrors = data && data.cartMetafieldsSet && data.cartMetafieldsSet.userErrors;
      if (errors || (userErrors && userErrors.length > 0)) {
        console.warn('[share-cart] Failed to set cart seller metafield', errors || userErrors);
      }
    } catch (error) {
      console.warn('[share-cart] Failed to set cart seller metafield', error);
    }
  }

  let ensureSellerAttributePromise = null;

  /**
   * Attaches the current seller's ID to a non-empty cart (seller_id attribute + the
   * custom.sales_person_id cart metafield) so a logged-in seller's normal orders are
   * attributed too, not just carts run through the share/restore flow. No-ops if there is
   * no seller, the cart is empty, or the attribute already matches. Runs once on page load
   * and again after every cart mutation (on:cart:add / on:line-item:change), since a
   * mutation can be the moment an empty cart first gets an item.
   */
  function ensureSellerAttribute() {
    const sellerId = getSellerId();
    if (!sellerId || ensureSellerAttributePromise) return;

    ensureSellerAttributePromise = (async () => {
      try {
        const cartResponse = await fetch(`${shopifyRoot()}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await cartResponse.json();
        if (!cart.items || cart.items.length === 0) return;
        if (cart.attributes && cart.attributes.seller_id === sellerId) return;

        const attributes = { ...cart.attributes, seller_id: sellerId };
        const updateResponse = await fetch(`${shopifyRoot()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes })
        });
        const updatedCart = await updateResponse.json();
        await setCartSellerMetafield(updatedCart.token, sellerId);
      } catch (error) {
        console.warn('[share-cart] Failed to attach seller attribute to cart', error);
      } finally {
        ensureSellerAttributePromise = null;
      }
    })();
  }

  document.addEventListener('on:cart:add', ensureSellerAttribute);
  document.addEventListener('on:line-item:change', ensureSellerAttribute);
  ensureSellerAttribute();

  // Re-open the cart drawer after a reload triggered by a drawer-initiated restore.
  try {
    if (sessionStorage.getItem(OPEN_DRAWER_KEY) === 'true') {
      sessionStorage.removeItem(OPEN_DRAWER_KEY);
      window.addEventListener('load', () => {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
      });
    }
  } catch (error) {
    /* sessionStorage unavailable — the drawer simply stays closed */
  }

  /**
   * Runs a request against the shared basket app proxy.
   * @param {string} base - Proxy base path, e.g. /apps/shared-basket/baskets.
   * @param {string} path - Path appended to the base, e.g. /ABCD-2345.
   * @param {object} [options] - fetch options.
   * @returns {Promise<{ok: boolean, status: number, data: object}>}
   */
  async function proxyRequest(base, path, options = {}) {
    const response = await fetch(`${base}${path}`, {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...options
    });
    let data = {};
    try {
      data = await response.json();
    } catch (error) {
      /* non-JSON response (e.g. proxy error page) — handled via response.status */
    }
    return { ok: response.ok, status: response.status, data };
  }

  /**
   * Formats a money amount using the storefront locale.
   * @param {string} amount - Decimal amount, e.g. "1499.00".
   * @param {string} currency - ISO currency code, e.g. "DKK".
   * @returns {string}
   */
  function formatMoney(amount, currency) {
    const locale = document.documentElement.lang || 'da';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(parseFloat(amount));
    } catch (error) {
      return `${amount} ${currency}`;
    }
  }

  let savedBasketsPromise = null;

  /**
   * Fetches the customer's saved baskets, once per page load. The cart drawer is fully
   * re-rendered on every cart change, so its loader element reconnects often — the
   * module-level cache avoids refetching the list each time.
   * @param {string} base - Proxy base path.
   * @returns {Promise<object[]>}
   */
  function fetchSavedBaskets(base) {
    if (!savedBasketsPromise) {
      savedBasketsPromise = proxyRequest(base, '', { method: 'GET' })
        .then(({ ok, data }) => (ok && data.baskets ? data.baskets : []))
        .catch(() => []);
    }
    return savedBasketsPromise;
  }

  /**
   * Replaces the current cart with the basket's items: clear, add line by line (so single
   * unavailable variants do not fail the whole restore), then note/attributes.
   * @param {object} basket - Basket details from the app proxy.
   * @returns {Promise<string[]>} Titles of the lines that could not be added.
   */
  async function applyBasketToCart(basket) {
    await fetch(`${shopifyRoot()}cart/clear.js`, { method: 'POST', headers: { Accept: 'application/json' } });

    const failedTitles = [];
    for (const item of basket.items) {
      if (!item.variantId || item.unavailable) {
        failedTitles.push(item.title);
        continue;
      }
      const line = { id: item.variantId, quantity: item.quantity };
      if (item.properties && Object.keys(item.properties).length > 0) line.properties = item.properties;
      const response = await fetch(`${shopifyRoot()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [line] })
      });
      if (!response.ok) failedTitles.push(item.title);
    }

    const attributes = basket.attributes && Object.keys(basket.attributes).length > 0 ? { ...basket.attributes } : {};
    const sellerId = getSellerId();
    if (sellerId) attributes.seller_id = sellerId;

    const update = {};
    if (basket.note) update.note = basket.note;
    if (Object.keys(attributes).length > 0) update.attributes = attributes;
    if (Object.keys(update).length > 0) {
      const response = await fetch(`${shopifyRoot()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(update)
      });
      if (attributes.seller_id) {
        const updatedCart = await response.json();
        await setCartSellerMetafield(updatedCart.token, attributes.seller_id);
      }
    }

    return failedTitles;
  }

  /**
   * Stores a "some items could not be added" warning to be shown after the post-restore reload.
   * @param {string} template - Localized warning with an [items] placeholder.
   * @param {string[]} failedTitles - Titles of the failed lines.
   */
  function storeRestoreWarning(template, failedTitles) {
    if (failedTitles.length === 0) return;
    try {
      sessionStorage.setItem(RESTORE_WARNING_KEY, template.replace('[items]', failedTitles.join(', ')));
    } catch (error) {
      /* sessionStorage unavailable — the warning is lost, the cart is still restored */
    }
  }

  /**
   * Restores a basket into the cart, then reloads so all cart surfaces re-render. When the
   * page has a cart drawer it is re-opened after the reload, so the whole flow stays in the
   * drawer. Used by the drawer loader and the guest basket search.
   * @param {object} basket - Basket details from the app proxy.
   * @param {string} warningTemplate - Localized warning with an [items] placeholder.
   */
  async function restoreBasketAndReload(basket, warningTemplate) {
    const failedTitles = await applyBasketToCart(basket);
    storeRestoreWarning(warningTemplate, failedTitles);
    if (document.getElementById('cart-drawer')) {
      try {
        sessionStorage.setItem(OPEN_DRAWER_KEY, 'true');
      } catch (error) {
        /* sessionStorage unavailable — the drawer stays closed after the reload */
      }
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('shared_basket');
    window.location.replace(url.toString());
  }

  const copyRevertTimers = new WeakMap();

  /**
   * Copies text to the clipboard and temporarily swaps the button label. The button's true
   * original label is cached on first use (data-original-text) rather than re-read from
   * textContent on every call — otherwise a click while the label still reads "Copied!"
   * would capture that as the "original" and get stuck reverting to it instead of the real
   * label. Any pending revert from a previous click is cleared first for the same reason.
   * @param {string} text - Text to copy.
   * @param {Element} button - Button that triggered the copy.
   */
  async function copyToClipboard(text, button) {
    if (button.dataset.originalText === undefined) button.dataset.originalText = button.textContent;
    const originalText = button.dataset.originalText;
    try {
      await navigator.clipboard.writeText(text);
      clearTimeout(copyRevertTimers.get(button));
      button.textContent = button.dataset.copiedText || originalText;
      copyRevertTimers.set(button, setTimeout(() => { button.textContent = originalText; }, 2000));
    } catch (error) {
      window.prompt(button.dataset.copiedText || '', text);
    }
  }

  if (!customElements.get('share-basket')) {
    class ShareBasket extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('.js-share-form');
        this.successPanel = this.querySelector('.js-share-success');
        this.errorEl = this.querySelector('.js-share-error');
        this.submitButton = this.querySelector('.js-share-submit');
        this.createdBasket = null;

        this.documentClickHandler = this.handleDocumentClick.bind(this);
        this.keydownHandler = this.handleKeydown.bind(this);
        document.addEventListener('click', this.documentClickHandler);
        document.addEventListener('keydown', this.keydownHandler);

        this.form.addEventListener('submit', this.handleSubmit.bind(this));
        this.querySelectorAll('.js-share-close').forEach((el) => {
          el.addEventListener('click', () => this.close());
        });
        this.querySelector('.js-share-done').addEventListener('click', () => this.close());
        this.querySelector('.js-share-copy').addEventListener('click', (evt) => {
          const urlInput = this.querySelector('.js-share-url');
          copyToClipboard(urlInput.value, evt.currentTarget);
        });
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.documentClickHandler);
        document.removeEventListener('keydown', this.keydownHandler);
      }

      handleDocumentClick(evt) {
        const trigger = evt.target.closest('[data-share-cart]');
        if (!trigger) return;
        evt.preventDefault();
        this.open();
      }

      handleKeydown(evt) {
        if (evt.key === 'Escape' && !this.hidden) this.close();
      }

      open() {
        this.hidden = false;
        document.body.classList.add('overflow-hidden');
        const emailInput = this.querySelector('.js-share-email');
        if (typeof trapFocus === 'function') trapFocus(this, emailInput || undefined);
        else if (emailInput) emailInput.focus();
      }

      close() {
        this.hidden = true;
        document.body.classList.remove('overflow-hidden');
        if (typeof removeTrapFocus === 'function') removeTrapFocus();
        // The cart was cleared after a successful share — reload (works on any page, the
        // dialog is global) so the cart page/drawer render the now-empty cart. Drop a
        // shared_basket parameter so the restore banner is not re-triggered.
        if (this.createdBasket) {
          const url = new URL(window.location.href);
          url.searchParams.delete('shared_basket');
          window.location.replace(url.toString());
        }
      }

      showError(message) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      async handleSubmit(evt) {
        evt.preventDefault();
        this.errorEl.hidden = true;
        this.submitButton.disabled = true;
        this.submitButton.classList.add('is-loading');
        try {
          await this.shareCart();
        } catch (error) {
          this.showError(this.dataset.textErrorGeneric);
        } finally {
          this.submitButton.disabled = false;
          this.submitButton.classList.remove('is-loading');
        }
      }

      async shareCart() {
        const cartResponse = await fetch(`${shopifyRoot()}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await cartResponse.json();
        if (!cart.items || cart.items.length === 0) {
          this.showError(this.dataset.textErrorEmpty);
          return;
        }

        const sellerId = getSellerId();
        if (sellerId && cart.attributes?.seller_id !== sellerId) {
          const attributes = { ...cart.attributes, seller_id: sellerId };
          await fetch(`${shopifyRoot()}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes })
          });
          cart.attributes = attributes;
        }
        if (sellerId) await setCartSellerMetafield(cart.token, sellerId);

        const payload = {
          email: this.querySelector('.js-share-email').value.trim(),
          locale: document.documentElement.lang || 'da',
          cart: {
            items: cart.items.map((item) => ({
              variantId: item.variant_id,
              quantity: item.quantity,
              properties: item.properties && Object.keys(item.properties).length > 0 ? item.properties : undefined
            })),
            note: cart.note || undefined,
            attributes: cart.attributes && Object.keys(cart.attributes).length > 0 ? cart.attributes : undefined
          }
        };
        if (sellerId) payload.sellerId = sellerId;

        const { ok, data } = await proxyRequest(this.dataset.proxyBase, '', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        if (!ok) {
          const code = data.error && data.error.code;
          if (code === 'INVALID_EMAIL') this.showError(this.dataset.textErrorEmail);
          else if (code === 'EMPTY_CART') this.showError(this.dataset.textErrorEmpty);
          else this.showError(this.dataset.textErrorGeneric);
          return;
        }

        // Clear the cart only now that the basket exists as a draft order.
        await fetch(`${shopifyRoot()}cart/clear.js`, { method: 'POST', headers: { Accept: 'application/json' } });
        this.createdBasket = data.basket;
        this.showSuccess(data.basket);
      }

      showSuccess(basket) {
        const shareUrl = basket.shareUrl || `${window.location.origin}${basket.sharePath}`;
        this.querySelector('.js-share-basket-id').textContent = basket.basketId;
        this.querySelector('.js-share-url').value = shareUrl;
        const emailNote = this.querySelector('.js-share-email-note');
        if (emailNote) emailNote.hidden = !basket.emailSent;
        const qrImage = this.querySelector('.js-share-qr');
        qrImage.src = `${this.dataset.proxyBase}/${encodeURIComponent(basket.basketId)}/qr`;
        qrImage.hidden = false;
        this.form.hidden = true;
        this.successPanel.hidden = false;
      }
    }

    customElements.define('share-basket', ShareBasket);
  }

  if (!customElements.get('shared-basket-restore')) {
    class SharedBasketRestore extends HTMLElement {
      connectedCallback() {
        this.messageEl = this.querySelector('.js-restore-message');

        this.showPendingWarning();

        const basketId = (new URLSearchParams(window.location.search).get('shared_basket') || '').trim().toUpperCase();
        if (BASKET_ID_PATTERN.test(basketId)) this.restoreFromUrl(basketId);
      }

      /** Shows the "some items could not be added" warning stored before the post-restore reload. */
      showPendingWarning() {
        let warning = null;
        try {
          warning = sessionStorage.getItem(RESTORE_WARNING_KEY);
          if (warning) sessionStorage.removeItem(RESTORE_WARNING_KEY);
        } catch (error) {
          /* sessionStorage unavailable — skip the warning */
        }
        if (warning) this.show(warning);
      }

      show(message) {
        this.messageEl.textContent = message;
        this.hidden = false;
      }

      /** Restores the linked basket into the cart immediately — opening a share link is the
       * confirmation; the banner only reports progress and errors. */
      async restoreFromUrl(basketId) {
        this.show(this.dataset.textRestoring);
        try {
          const { ok, status, data } = await proxyRequest(this.dataset.proxyBase, `/${encodeURIComponent(basketId)}`, { method: 'GET' });
          if (status === 404) {
            this.show(this.dataset.textNotFound);
          } else if (status === 410) {
            this.show(this.dataset.textGone);
          } else if (!ok) {
            this.show(this.dataset.textError);
          } else {
            await restoreBasketAndReload(data.basket, this.dataset.textWarning);
          }
        } catch (error) {
          this.show(this.dataset.textError);
        }
      }
    }

    customElements.define('shared-basket-restore', SharedBasketRestore);
  }

  if (!customElements.get('saved-baskets')) {
    class SavedBaskets extends HTMLElement {
      connectedCallback() {
        this.listEl = this.querySelector('.js-saved-baskets-list');
        this.load();
      }

      async load() {
        const baskets = await fetchSavedBaskets(this.dataset.proxyBase);
        if (baskets.length === 0) return;
        for (const basket of baskets) {
          this.listEl.appendChild(this.buildRow(basket));
        }
        this.hidden = false;
      }

      /**
       * Builds one saved basket row (no innerHTML — basket data is API-provided).
       * @param {object} basket - Basket summary from the app proxy.
       * @returns {Element}
       */
      buildRow(basket) {
        const row = document.createElement('li');
        row.className = 'saved-baskets__row';

        const details = document.createElement('div');
        details.className = 'saved-baskets__details';

        const title = document.createElement('span');
        title.className = 'saved-baskets__id font-bold';
        title.textContent = basket.basketId;
        details.appendChild(title);

        const meta = document.createElement('span');
        meta.className = 'saved-baskets__meta';
        const locale = document.documentElement.lang || 'da';
        const createdAt = new Date(basket.createdAt).toLocaleDateString(locale);
        const parts = [createdAt];
        if (basket.itemCount !== null) parts.push(this.dataset.textItems.replace('[count]', String(basket.itemCount)));
        parts.push(formatMoney(basket.totalPrice, basket.currency));
        meta.textContent = parts.join(' · ');
        details.appendChild(meta);

        const actions = document.createElement('div');
        actions.className = 'saved-baskets__actions';

        const loadLink = document.createElement('a');
        loadLink.className = 'btn btn--secondary';
        loadLink.href = basket.sharePath;
        loadLink.textContent = this.dataset.textLoad;
        actions.appendChild(loadLink);

        const copyButton = document.createElement('button');
        copyButton.type = 'button';
        copyButton.className = 'link saved-baskets__copy';
        copyButton.dataset.copiedText = this.dataset.textCopied;
        copyButton.textContent = this.dataset.textCopy;
        copyButton.addEventListener('click', () => {
          copyToClipboard(`${window.location.origin}${basket.sharePath}`, copyButton);
        });
        actions.appendChild(copyButton);

        row.appendChild(details);
        row.appendChild(actions);
        return row;
      }
    }

    customElements.define('saved-baskets', SavedBaskets);
  }

  if (!customElements.get('saved-baskets-tabs')) {
    const CHEVRON_SVG = '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" focusable="false"><path d="M2 4l4 4 4-4" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>';
    const basketDetailsCache = new Map();

    class SavedBasketsTabs extends HTMLElement {
      connectedCallback() {
        this.drawer = this.closest('cart-drawer');
        this.cartTab = this.querySelector('.js-tab-cart');
        this.savedTab = this.querySelector('.js-tab-saved');
        this.savedCount = this.querySelector('.js-saved-count');
        this.panel = this.querySelector('.js-saved-panel');
        this.listEl = this.querySelector('.js-saved-list');
        this.warningEl = this.querySelector('.js-saved-warning');

        this.cartTab.addEventListener('click', () => this.selectTab(false));
        this.savedTab.addEventListener('click', () => this.selectTab(true));

        this.populate();
        this.showPendingWarning();
      }

      disconnectedCallback() {
        // The drawer re-renders on cart changes — never leave the content hidden behind.
        if (this.drawer) this.drawer.classList.remove('cart-drawer--saved-open');
      }

      /** Shows a warning left by a previous restore (visible on both tabs; the cart page
       * uses the restore banner instead — the drawer never renders there). */
      showPendingWarning() {
        let warning = null;
        try {
          warning = sessionStorage.getItem(RESTORE_WARNING_KEY);
          if (warning) sessionStorage.removeItem(RESTORE_WARNING_KEY);
        } catch (error) {
          /* sessionStorage unavailable — skip the warning */
        }
        if (warning) {
          this.warningEl.textContent = warning;
          this.warningEl.hidden = false;
          this.hidden = false;
        }
      }

      selectTab(showSaved) {
        this.cartTab.classList.toggle('is-active', !showSaved);
        this.cartTab.setAttribute('aria-selected', String(!showSaved));
        this.savedTab.classList.toggle('is-active', showSaved);
        this.savedTab.setAttribute('aria-selected', String(showSaved));
        this.panel.hidden = !showSaved;
        if (this.drawer) this.drawer.classList.toggle('cart-drawer--saved-open', showSaved);
        if (showSaved) this.prefetchDetails();
      }

      /** Warms the details cache for all baskets as soon as the saved tab is opened, so
       * expanding a card renders instantly instead of waiting for the proxy request. */
      prefetchDetails() {
        for (const basket of this.baskets) {
          this.fetchDetails(basket.basketId).catch(() => {
            /* prefetch is best-effort — expanding the card retries and shows the error */
          });
        }
      }

      /**
       * Fetches one basket's details through a module-level promise cache (shared across
       * drawer re-renders, deduped between prefetch and expand). Definitive misses (404/410)
       * stay cached; transient failures are evicted so the next expand retries.
       * @param {string} basketId - The basket to load.
       * @returns {Promise<{ok: boolean, status: number, basket: object|null}>}
       */
      fetchDetails(basketId) {
        let promise = basketDetailsCache.get(basketId);
        if (!promise) {
          promise = proxyRequest(this.dataset.proxyBase, `/${encodeURIComponent(basketId)}`, { method: 'GET' })
            .then((result) => {
              if (!result.ok && result.status !== 404 && result.status !== 410) basketDetailsCache.delete(basketId);
              return { ok: result.ok, status: result.status, basket: result.ok ? result.data.basket : null };
            })
            .catch((error) => {
              basketDetailsCache.delete(basketId);
              throw error;
            });
          basketDetailsCache.set(basketId, promise);
        }
        return promise;
      }

      async populate() {
        const baskets = await fetchSavedBaskets(this.dataset.proxyBase);
        this.baskets = baskets;
        if (baskets.length === 0) return;
        this.savedCount.textContent = String(baskets.length);
        for (const basket of baskets) {
          this.listEl.appendChild(this.buildCard(basket));
        }
        this.hidden = false;
      }

      /** Collapses every open card except the given one — only one basket is open at a time. */
      collapseOthers(activeCard) {
        for (const card of this.listEl.querySelectorAll('.saved-basket-card.is-open')) {
          if (card !== activeCard) this.collapseCard(card);
        }
      }

      /**
       * Builds one collapsible basket card (no innerHTML for API-provided values).
       * @param {object} basket - Basket summary from the app proxy.
       * @returns {Element}
       */
      buildCard(basket) {
        const card = document.createElement('li');
        card.className = 'saved-basket-card';

        const header = document.createElement('button');
        header.type = 'button';
        header.className = 'saved-basket-card__header';
        header.setAttribute('aria-expanded', 'false');

        const info = document.createElement('span');
        info.className = 'saved-basket-card__info';
        const title = document.createElement('strong');
        title.className = 'saved-basket-card__id';
        title.textContent = basket.basketId;
        const meta = document.createElement('span');
        meta.className = 'saved-basket-card__meta';
        const locale = document.documentElement.lang || 'da';
        const parts = [new Date(basket.createdAt).toLocaleDateString(locale)];
        if (basket.itemCount !== null) parts.push(this.dataset.textItemsSuffix.replace('[count]', String(basket.itemCount)));
        meta.textContent = parts.join(' · ');
        info.appendChild(title);
        info.appendChild(meta);

        const total = document.createElement('span');
        total.className = 'saved-basket-card__total';
        total.textContent = formatMoney(basket.totalPrice, basket.currency);
        const chevron = document.createElement('span');
        chevron.className = 'saved-basket-card__chevron';
        chevron.innerHTML = CHEVRON_SVG;

        header.appendChild(info);
        header.appendChild(total);
        header.appendChild(chevron);

        const details = document.createElement('div');
        details.className = 'saved-basket-card__details has-motion';
        details.hidden = true;
        const content = document.createElement('div');
        content.className = 'saved-basket-card__content';
        details.appendChild(content);

        // Same height-transition technique as the theme's details-disclosure accordions:
        // animate 0 <-> scrollHeight, then drop the inline height once the motion ends.
        details.addEventListener('transitionend', (evt) => {
          if (evt.target !== details) return;
          if (!card.classList.contains('is-open')) details.hidden = true;
          details.style.height = '';
        });

        header.addEventListener('click', () => {
          // is-open is set synchronously and is the single source of truth — details.hidden
          // lags behind while the close animation runs, so it must not drive the toggle.
          if (card.classList.contains('is-open')) {
            this.collapseCard(card);
          } else {
            this.collapseOthers(card);
            this.expandCard(card, header, details);
            if (!details.dataset.loaded) this.loadDetails(basket.basketId, details, content);
          }
        });

        card.appendChild(header);
        card.appendChild(details);
        return card;
      }

      /** Mirrors the theme's DetailsDisclosure check — with reduced motion the transition
       * duration is zero and transitionend never fires, so toggle without animating. */
      static transitionsEnabled(details) {
        return window.getComputedStyle(details).transitionDuration !== '0s';
      }

      expandCard(card, header, details) {
        card.classList.add('is-open');
        header.setAttribute('aria-expanded', 'true');
        const wasHidden = details.hidden;
        details.hidden = false;
        if (SavedBasketsTabs.transitionsEnabled(details)) {
          // Re-expanding a card that is mid-close animates from its current height instead
          // of snapping to zero first.
          if (wasHidden) details.style.height = '0';
          requestAnimationFrame(() => {
            if (!card.classList.contains('is-open')) return; // direction changed mid-frame
            details.style.height = `${details.scrollHeight}px`;
          });
        } else {
          details.style.height = '';
        }
      }

      collapseCard(card) {
        const header = card.querySelector('.saved-basket-card__header');
        const details = card.querySelector('.saved-basket-card__details');
        card.classList.remove('is-open');
        header?.setAttribute('aria-expanded', 'false');
        if (!details || details.hidden) return;
        if (SavedBasketsTabs.transitionsEnabled(details)) {
          details.style.height = `${details.scrollHeight}px`;
          requestAnimationFrame(() => {
            if (card.classList.contains('is-open')) return; // direction changed mid-frame
            details.style.height = '0';
          });
        } else {
          details.hidden = true;
          details.style.height = '';
        }
      }

      /**
       * Renders the basket's line items + restore button on first expand. The details are
       * usually already prefetched (see prefetchDetails), so this resolves instantly.
       * @param {string} basketId - The basket to load.
       * @param {Element} details - The card's animated panel.
       * @param {Element} content - The panel's inner content wrapper.
       */
      async loadDetails(basketId, details, content) {
        details.dataset.loaded = 'true';
        content.textContent = '…';

        let result;
        try {
          result = await this.fetchDetails(basketId);
        } catch (error) {
          content.textContent = this.dataset.textError;
          delete details.dataset.loaded;
          this.refreshPanelHeight(details);
          return;
        }
        if (!result.ok) {
          if (result.status === 404) content.textContent = this.dataset.textNotFound;
          else if (result.status === 410) content.textContent = this.dataset.textGone;
          else content.textContent = this.dataset.textError;
          delete details.dataset.loaded;
          this.refreshPanelHeight(details);
          return;
        }
        const basket = result.basket;

        content.textContent = '';
        const items = document.createElement('ul');
        items.className = 'saved-basket-card__items';
        items.setAttribute('role', 'list');
        for (const item of basket.items) {
          const row = document.createElement('li');
          row.className = 'saved-basket-card__item';
          const label = document.createElement('span');
          label.textContent = `${item.title} × ${item.quantity}`;
          const price = document.createElement('span');
          price.textContent = formatMoney(item.linePrice, basket.currency);
          row.appendChild(label);
          row.appendChild(price);
          items.appendChild(row);
        }
        content.appendChild(items);

        const restoreButton = document.createElement('button');
        restoreButton.type = 'button';
        restoreButton.className = 'btn btn--secondary w-full';
        restoreButton.textContent = this.dataset.textRestore || '';
        restoreButton.addEventListener('click', async () => {
          restoreButton.disabled = true;
          restoreButton.textContent = this.dataset.textRestoring;
          try {
            await restoreBasketAndReload(basket, this.dataset.textWarning);
          } catch (error) {
            restoreButton.disabled = false;
            restoreButton.textContent = this.dataset.textRestore || '';
            this.warningEl.textContent = this.dataset.textError;
            this.warningEl.hidden = false;
          }
        });
        content.appendChild(restoreButton);
        this.refreshPanelHeight(details);
      }

      /** Re-measures the panel's animated height when its content changes mid-transition
       * (e.g. the '…' placeholder is replaced by the loaded line items). */
      refreshPanelHeight(details) {
        if (!details.hidden && details.style.height) {
          details.style.height = `${details.scrollHeight}px`;
        }
      }
    }

    customElements.define('saved-baskets-tabs', SavedBasketsTabs);
  }

})();
